import { useMutation, useQueryClient } from '@tanstack/react-query'
import { getContent, saveContent, type ContentEntity, type ContentListResult } from '../../lib/api/content'

type El = Record<string, unknown>

/**
 * Flip a content entity's `enabled` flag (the "Attivo/Attiva" status on conventions and
 * discount bands). Round-trips the FULL entity via getContent() before saving so translations
 * and every other element are preserved untouched — same contract the rate drawer uses.
 *
 * The list cache (keyed by `listKey`) is patched optimistically so the toggle flips instantly,
 * and rolled back if the save fails.
 */
export function useToggleEnabled(model: string, listKey: readonly unknown[]) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      const full = await getContent(model, id)
      const next: ContentEntity = { ...full, elements: { ...(full.elements as El), enabled } }
      await saveContent(model, next)
    },
    onMutate: async ({ id, enabled }) => {
      await qc.cancelQueries({ queryKey: listKey })
      const prev = qc.getQueryData<ContentListResult>(listKey)
      qc.setQueryData<ContentListResult>(listKey, (old) =>
        old
          ? {
              ...old,
              data: old.data.map((r) =>
                r.id === id ? { ...r, elements: { ...(r.elements as El), enabled } } : r,
              ),
            }
          : old,
      )
      return { prev }
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(listKey, ctx.prev)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: listKey })
    },
  })
}
